'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { SetScore, TeamGames } from '@/types/tournament.model';

interface SetScoreEditorProps {
  team1Name: string;
  team2Name: string;
  setScores: SetScore[];
  onSetScoreChange: (index: number, team: TeamGames, value: number) => void;
  onAddSet: () => void;
  onRemoveSet: (index: number) => void;
}

export function SetScoreEditor({
  team1Name,
  team2Name,
  setScores,
  onSetScoreChange,
  onAddSet,
  onRemoveSet,
}: SetScoreEditorProps) {
  const adjustScore = (index: number, team: TeamGames, increment: boolean) => {
    const currentValue = setScores[index][team];
    const newValue = increment
      ? currentValue + 1
      : Math.max(currentValue - 1, 0);
    onSetScoreChange(index, team, newValue);
  };

  // A text input rather than type="number": React leaves "06" on screen when the
  // field held 0 (it compares "06" == 6 loosely), and a focused number input
  // changes its value on mouse wheel while the modal scrolls.
  const handleScoreTyping = (index: number, team: TeamGames, raw: string) => {
    if (!/^\d*$/.test(raw)) return;
    onSetScoreChange(index, team, Number(raw));
  };

  // Selecting on click too, not only on focus: the first score is already
  // focused when the modal opens, so clicking it fires no focus event.
  const selectAll = (e: React.SyntheticEvent<HTMLInputElement>) =>
    e.currentTarget.select();

  // The -/+ buttons stay out of the Tab order so Tab goes from score to score;
  // the arrow keys do their job from the keyboard.
  const renderScoreField = (index: number, team: TeamGames) => (
    <div className="flex items-center">
      <Button
        variant="outline"
        size="icon"
        tabIndex={-1}
        className="h-9 w-9 rounded-r-none border-r-0"
        onClick={() => adjustScore(index, team, false)}
        disabled={setScores[index][team] <= 0}
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="text"
        inputMode="numeric"
        maxLength={2}
        value={setScores[index][team]}
        onChange={(e) => handleScoreTyping(index, team, e.target.value)}
        onFocus={selectAll}
        onClick={selectAll}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          adjustScore(index, team, e.key === 'ArrowUp');
        }}
        className="h-9 rounded-none border-x-0 text-center focus-visible:border-indigo-500/50 focus-visible:ring-2 focus-visible:ring-indigo-500/30"
      />
      <Button
        variant="outline"
        size="icon"
        tabIndex={-1}
        className="h-9 w-9 rounded-l-none border-l-0"
        onClick={() => adjustScore(index, team, true)}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[40px_1fr_40px_1fr_40px] items-center gap-2">
        <div className="text-center text-xs font-medium text-gray-500">Set</div>
        <div className="text-center text-sm font-medium">{team1Name}</div>
        <div></div>
        <div className="text-center text-sm font-medium">{team2Name}</div>
        <div></div>
      </div>

      {setScores.map((_, index) => (
        <div
          key={index}
          className="grid grid-cols-[40px_1fr_40px_1fr_40px] items-center gap-2"
        >
          <div className="flex h-9 items-center justify-center rounded-md bg-gray-100 text-sm font-medium">
            {index + 1}
          </div>

          {renderScoreField(index, TeamGames.TEAM_1)}

          <div className="text-center text-xs font-medium">vs</div>

          {renderScoreField(index, TeamGames.TEAM_2)}

          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            onClick={() => onRemoveSet(index)}
            disabled={setScores.length <= 1}
            title="Eliminar set"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        className="mt-4 w-full"
        onClick={onAddSet}
      >
        <Plus className="mr-1 h-4 w-4" />
        Añadir Set
      </Button>
    </div>
  );
}
